import { Input as HeroUiInput, InputProps as HeroUiInputProps } from '@heroui/react';
import { FocusEvent, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@shared/components/Icon';
import { labelInputStyles } from '@ui/Input/styles';

/**
 * `defaultValue` is deliberately omitted: this wrapper always drives the underlying HeroUI input
 * from its own `localValue` state, so an uncontrolled initial value would be silently dropped.
 * Pass `value` and `onValueChange` instead.
 */
export interface InputProps extends Omit<HeroUiInputProps, 'defaultValue'> {
  rounded?: 'medium' | 'full';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function InputWithRef(
  {
    isClearable,
    endContent,
    startContent,
    isInvalid,
    rounded,
    classNames = {},
    onClear,
    labelPlacement,
    autoFocus,
    value,
    onValueChange,
    onFocus,
    onBlur,
    ...props
  }: InputProps,
  forwardedRef: React.Ref<HTMLInputElement>,
) {
  const placementOfLabel = labelPlacement ?? 'outside-top';
  const internalRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(false);

  const mergedRef = useCallback(
    (node: HTMLInputElement | null) => {
      (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [forwardedRef],
  );

  useEffect(() => {
    if (autoFocus) {
      internalRef.current?.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!isFocused && localValue !== value) {
      setLocalValue(value ?? '');
    }
  }, [value, localValue, isFocused]);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  const handleValueChange = useCallback(
    (v: string) => {
      setLocalValue(v);
      onValueChange?.(v);
    },
    [onValueChange],
  );

  let radius: 'md' | 'full' | undefined;
  if (rounded === 'medium') {
    radius = 'md';
  } else if (rounded === 'full') {
    radius = 'full';
  }

  return (
    <HeroUiInput
      ref={mergedRef}
      {...props}
      radius={radius}
      value={localValue}
      onValueChange={handleValueChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      labelPlacement={placementOfLabel}
      isClearable={isClearable}
      isInvalid={isInvalid}
      onClear={isClearable ? onClear : undefined}
      endContent={
        isClearable ? (
          <Icon name="remove-filled" gridSize={4.25} className="text-coast-purple" />
        ) : (
          endContent
        )
      }
      startContent={startContent}
      classNames={{
        ...classNames,
        input: [
          'bg-transparent text-md',
          'data-[has-end-content=true]:pe-8',
          'placeholder:text-gray-40',
          '!outline-none',
          !isInvalid && 'selection:bg-coast-green-10',
          isInvalid && 'selection:bg-tropical-red-40',
          classNames.input,
        ],

        inputWrapper: [
          'h-12',
          'bg-gray-background',
          'data-[hover=true]:bg-gray-background',
          'data-[focus=true]:border-coast-green',
          'border-solid px-4',
          'border-1',
          'border-transparent',
          'group-data-[invalid=true]:border-tropical-red',
          'group-data-[invalid=true]:bg-white',
          'group-data-[invalid=true]:border-solid',
          'group-data-[invalid=true]:border-1',
          startContent && 'pl-2',
          classNames.inputWrapper,
        ],
        clearButton: ['p-0', classNames.clearButton],
        description: ['text-sm', 'px-4', classNames.description],
        errorMessage: ['text-sm', 'px-4', classNames.errorMessage],
        label: [placementOfLabel === 'outside-top' ? labelInputStyles : '', classNames.label],
      }}
    />
  );
});
